import React, { useState, useRef, useEffect } from 'react'
import { Bar } from '@vx/shape'
import { localPoint } from '@vx/event'
import { useSpring, useSprings, animated } from 'react-spring'

import { useTooltipDisplayContext } from '../../common/tooltip/tooltip.context'

import { useStyles } from './hover-markers.style'
import { lineChartService } from './line-chart.service'
import { formatDataForTooltip } from './format.service'

const hoverPadding = 20

export const HoverMarkers = function ({
  series,
  scales,
  dimensions,
  svgMargin,
  formatters,
  yFormat,
  y2Format,
  yCIFormat,
  seriesToHide = {},
  missingPointDisplay,
  onMousePosChange = function () {},
  onClick = function () {},
  onHover = function () {},
  onPointUpdate = function () {},
  currentHover
}) {
  const classes = useStyles()

  const currentHoverRef = useRef(null)
  const isHoveredRef = useRef(false)

  const [xValueClosest, setXValueClosest] = useState(null)
  const [hoverGroupProps, setHoverGroupProps] = useSpring(() => ({ opacity: 0 }))
  // x is a property of the text label element above the line and x1/x2 are properties of the line element itself
  const [hoverLineProps, setHoverLineProps] = useSpring(() => ({}))
  const [hoverCircleProps, setHoverCircleProps] = useSprings(series.length, () => ({}))
  const [, setTooltipDisplayData] = useTooltipDisplayContext()

  const { width, height } = dimensions

  useEffect(() => {
    if (currentHover && currentHover[0] && currentHover[0].current && currentHover[0].current.x !== undefined && currentHover !== currentHoverRef.current) {
      const xValue = currentHover[0].current.x
      const closestPoints = lineChartService.findClosestPoints(series, xValue)
      setHover(closestPoints)
    } else if (currentHover === null && currentHoverRef.current) {
      handleMouseLeave()
    }
    currentHoverRef.current = currentHover
  }, [currentHover])

  function handleMouseMove (event) {
    isHoveredRef.current = true
    if (!event.target || !event.target.ownerSVGElement) return

    const { x: xPixelExact } = localPoint(event)
    const xValueExact = scales.x.invert(xPixelExact - svgMargin)
    const closestPoints = lineChartService.findClosestPoints(series, xValueExact)
    setHover(closestPoints)
    onMousePosChange(xPixelExact)
    emitHoverEvent(closestPoints)
  }

  function handleMouseLeave () {
    isHoveredRef.current = false
    setTooltipDisplayData()
    setHoverGroupProps({ opacity: 0 })
    emitHoverEvent(null)
  }

  function handleClick (event) {
    if (!event.target || !event.target.ownerSVGElement) return

    const { x: xPixelExact } = localPoint(event)
    const xValueExact = scales.x.invert(xPixelExact - svgMargin)
    const closestPoints = lineChartService.findClosestPoints(series, xValueExact)
    onClick(closestPoints)
  }

  function emitHoverEvent (points) {
    currentHoverRef.current = points
    onHover(points)
  }

  function setHover (closestPoints) {
    const firstClosestPoint = closestPoints.find(({ current }) => current)

    if (!firstClosestPoint) {
      return
    }

    const xValueClosest = firstClosestPoint.current.x
    const xPixelClosest = scales.x(xValueClosest)

    let minPoint = { x: Infinity, y: Infinity }

    setHoverLineProps({ x: xPixelClosest, x1: xPixelClosest, x2: xPixelClosest })
    setHoverCircleProps((index) => {
      const point = closestPoints[index]
      let closestPoint = point.current ? point.current : (point.previous) ? point.previous : point.next
      if (closestPoint) {
        const cx = scales.x(closestPoint.x)
        const cy = scales.y(closestPoint.y)
        const isValidPoint = !isNaN(cx) && !isNaN(cy)
        if (Math.min(minPoint.y, cy) < minPoint.y && !seriesToHide[point.name]) {
          minPoint = { x: cx, y: cy }
        }
        return {
          cx: isValidPoint ? cx : 0,
          cy: isValidPoint ? cy : 0,
          visibility: point.current && isValidPoint ? 'visible' : 'hidden'
        }
      } else {
        return {}
      }
    })
    if (!isHoveredRef.current && minPoint.y !== Infinity && minPoint.x !== Infinity) {
      onPointUpdate([minPoint.x, minPoint.y])
    }
    setHoverGroupProps({ opacity: 1 })
    setXValueClosest(xValueClosest)

    const tooltipDisplayData = formatDataForTooltip({
      closestPoints,
      series,
      primaryValueFormat: yFormat,
      secondaryValueFormat: y2Format,
      ciFormat: yCIFormat,
      seriesToHide,
      missingPointDisplay
    })
    setTooltipDisplayData({ list: tooltipDisplayData })
  }

  return (
    <>
      <animated.g style={hoverGroupProps}>
        <animated.text
          y={-7}
          className={classes.hoverTopLabel}
          x={hoverLineProps.x}
        >
          {formatters.xWithUnit(xValueClosest)}
        </animated.text>
        <animated.line
          y1={0}
          y2={height}
          stroke="gray"
          strokeWidth={1}
          strokeDasharray="5,4"
          x1={hoverLineProps.x1}
          x2={hoverLineProps.x2}
        />
        { series.map(({ id, name, label, color }, i) => {
          if (lineChartService.isSeriesHidden({ name, label }, seriesToHide)) {
            return null
          }

          const springProps = hoverCircleProps[i]
          return (
            <animated.circle key={id}
              r={4}
              fill={color}
              stroke={color}
              strokeWidth={2}
              {...springProps}
            />
          )
        })}
      </animated.g>
      <Bar
        x={-hoverPadding}
        y={-hoverPadding}
        width={width + (hoverPadding * 2)}
        height={height + (hoverPadding * 2)}
        fill="transparent"
        rx={14}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      />
    </>
  )
}
